import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  Ban,
  BellRing,
  Copy,
  Download,
  FileText,
  Loader2,
  Mail,
  MessageCircle,
  Pencil,
  Plus,
  Send,
  Trash2,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PER_PAGE, Pager } from "@/components/Pager";
import { api, downloadFile, errorMessage, openPdf } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { KIND_LABEL, formatDate, money, todayISO } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const BLANK_PAY = {
  amount: "",
  paid_on: todayISO(),
  has_tds: false,
  inward_amount: "",
  tds_amount: "",
  mode: "",
  reference: "",
  remitter: "",
  instrument_date: "",
  remark: "",
  show_remittance_on_receipt: false,
};

const PAY_MODES = [
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
  { value: "dd", label: "Demand draft" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];

const Row = ({ label, value }) => (
  <div>
    <dt className="label-caps">{label}</dt>
    <dd className="mt-1 text-[13px] text-slate-800">{value || "—"}</dd>
  </div>
);

const ItemTable = ({ title, items, symbol }) => (
  <div
    className="mt-6 min-w-0"
    data-testid={`items-${title.toLowerCase().includes("dis") ? "disbursements" : "fees"}`}
  >
    <p className="label-caps">{title}</p>
    <div className="mt-3 max-w-full overflow-x-auto border border-slate-200">
      <table className="w-full min-w-[560px] text-left">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="label-caps px-4 py-2.5">Particulars</th>
            <th className="label-caps px-4 py-2.5 text-right">Qty</th>
            <th className="label-caps px-4 py-2.5 text-right">Rate</th>
            <th className="label-caps px-4 py-2.5 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((item) => (
            <tr key={item.item_id}>
              <td className="px-4 py-3">
                <p className="text-[13px] text-slate-900">
                  {item.description || KIND_LABEL[item.kind]}
                </p>
                <p className="text-[11px] text-slate-400">
                  {KIND_LABEL[item.kind]}
                  {item.detail ? ` · ${item.detail}` : ""}
                  {item.taxable ? "" : " · not taxed"}
                </p>
              </td>
              <td className="num px-4 py-3 text-right text-[12px] text-slate-600">
                {Number(item.quantity)}
              </td>
              <td className="num px-4 py-3 text-right text-[12px] text-slate-600">
                {money(item.rate, symbol)}
              </td>
              <td className="num px-4 py-3 text-right text-[13px] text-slate-900">
                {money(item.amount, symbol)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payForm, setPayForm] = useState(BLANK_PAY);
  const [payPage, setPayPage] = useState(1);
  const [remitOpen, setRemitOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const { user } = useAuth();
  const isOwner = user?.role !== "clerk";

  const load = useCallback(async () => {
    try {
      const [res, payRes] = await Promise.all([
        api.get(`/invoices/${invoiceId}`),
        api.get(`/invoices/${invoiceId}/payments`),
      ]);
      setData(res.data);
      setPayments(payRes.data.payments || []);
      setPayForm({ amount: "", paid_on: todayISO() });
    } catch (error) {
      toast.error(errorMessage(error, "Could not load invoice"));
      navigate("/invoices", { replace: true });
    }
  }, [invoiceId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="invoice-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const { invoice, case: matter, contact, firm, settings } = data;
  const client = data.client || {};
  const recipients = data.recipients || [];
  const copies = settings.cc_emails || [];
  const revisionPending =
    invoice.status !== "draft" && Number(invoice.revision || 0) > Number(invoice.revision_sent || 0);
  const symbol = settings.currency_symbol || "Rs.";
  const fees = invoice.items.filter((i) => i.kind !== "disbursement");
  const disbursements = invoice.items.filter((i) => i.kind === "disbursement");
  const balance = Number(invoice.balance_due ?? invoice.total - invoice.amount_paid);
  const unpaid = ["sent", "partial", "overdue"].includes(invoice.display_status);

  const act = async (key, fn, successMessage) => {
    setBusy(key);
    try {
      const result = await fn();
      if (successMessage) toast.success(successMessage);
      return result;
    } catch (error) {
      toast.error(errorMessage(error, "Action failed"));
      return null;
    } finally {
      setBusy("");
    }
  };

  const setStatus = (status) =>
    act(status, async () => {
      await api.post(`/invoices/${invoiceId}/status`, { status });
      await load();
    }, status === "paid" ? "Marked as paid" : `Marked as ${status}`);

  const sendEmail = () =>
    act("email", async () => {
      const { data: res } = await api.post(`/invoices/${invoiceId}/send-email`);
      await load();
      toast.success(`Invoice emailed to ${res.recipient}`);
    });

  const sendWhatsApp = () =>
    act("whatsapp", async () => {
      const { data: res } = await api.post(`/invoices/${invoiceId}/whatsapp`);
      openWhatsApp(res);
      await load();
    });

  const sendReminder = () =>
    act("reminder", async () => {
      const { data: res } = await api.post(`/invoices/${invoiceId}/send-reminder`);
      await load();
      toast.success(`Reminder sent to ${res.recipient}`);
    });

  const addPayment = (event) => {
    event.preventDefault();
    const inward = Number(payForm.inward_amount || 0);
    const tds = Number(payForm.tds_amount || 0);
    const amount = payForm.has_tds ? inward + tds : Number(payForm.amount);
    if (payForm.has_tds && tds <= 0) {
      toast.error("Enter the TDS deducted, or untick TDS");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Enter the amount received");
      return;
    }
    return act("payment", async () => {
      await api.post(`/invoices/${invoiceId}/payments`, {
        amount,
        paid_on: payForm.paid_on || todayISO(),
        has_tds: payForm.has_tds,
        inward_amount: inward,
        tds_amount: tds,
        mode: payForm.mode,
        reference: payForm.reference,
        remitter: payForm.remitter,
        instrument_date: payForm.instrument_date || null,
        remark: payForm.remark,
        show_remittance_on_receipt: payForm.show_remittance_on_receipt,
      });
      setPayForm({ ...BLANK_PAY, paid_on: payForm.paid_on });
      setRemitOpen(false);
      await load();
      toast.success("Payment recorded");
    });
  };

  const writeOff = () => {
    const reason = window.prompt(
      `Write off ${invoice.invoice_number}? It will stop counting as outstanding but keeps its number and receipts.\n\nReason (optional):`,
      invoice.written_off_reason || "",
    );
    if (reason === null) return;
    return act("write-off", async () => {
      await api.post(`/invoices/${invoiceId}/write-off`, { reason });
      await load();
      toast.success("Fee note written off");
    });
  };

  const restore = () =>
    act("restore", async () => {
      await api.post(`/invoices/${invoiceId}/restore`);
      await load();
      toast.success("Fee note restored to the books");
    });

  const removePayment = (paymentId) =>    act(`del-${paymentId}`, async () => {
      await api.delete(`/invoices/${invoiceId}/payments/${paymentId}`);
      await load();
      toast.success("Payment removed");
    });

  const remove = async () => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}?`)) return;
    await act("delete", async () => {
      await api.delete(`/invoices/${invoiceId}`);
      navigate("/invoices", { replace: true });
    }, "Invoice deleted");
  };

  return (
    <div className="space-y-8" data-testid="invoice-detail-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link
            to="/invoices"
            data-testid="back-to-invoices"
            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
            All invoices
          </Link>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <h1 className="num break-all font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
              {invoice.invoice_number}
            </h1>
            <StatusBadge status={invoice.display_status} testId="detail-status-badge" />
            {Number(invoice.revision || 0) > 0 && (
              <span
                className="border border-slate-300 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600"
                data-testid="revision-chip"
              >
                Revised &times;{invoice.revision}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-600">
            {matter.title} · {matter.forum || "Forum not set"}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 no-print">
          <button
            type="button"
            onClick={() => openPdf(`/invoices/${invoiceId}/pdf`)}
            data-testid="preview-pdf-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
            Preview PDF
          </button>
          <button
            type="button"
            onClick={() =>
              downloadFile(`/invoices/${invoiceId}/pdf`, `${invoice.invoice_number}.pdf`)
            }
            data-testid="download-pdf-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
            Download
          </button>
          <button
            type="button"
            onClick={sendEmail}
            disabled={busy === "email"}
            data-testid="send-email-button"
            className="inline-flex items-center gap-2 bg-slate-900 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
          >
            {busy === "email" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
            Email invoice
          </button>
          <button
            type="button"
            onClick={sendWhatsApp}
            disabled={busy === "whatsapp"}
            data-testid="send-whatsapp-button"
            className="inline-flex items-center gap-2 bg-emerald-700 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-emerald-800 disabled:opacity-60"
          >
            {busy === "whatsapp" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
            WhatsApp
          </button>
          {unpaid && (
            <button
              type="button"
              onClick={sendReminder}
              disabled={busy === "reminder"}
              data-testid="send-reminder-button"
              className={`inline-flex items-center gap-2 border px-4 py-2.5 text-[12px] font-semibold transition-colors duration-150 disabled:opacity-60 ${
                invoice.display_status === "overdue"
                  ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {busy === "reminder" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <BellRing className="h-3.5 w-3.5" strokeWidth={1.8} />
              )}
              Send reminder
            </button>
          )}

        </div>
      </div>

      <div className="flex flex-wrap gap-2 no-print" data-testid="status-actions">        {invoice.status !== "paid" && (
          <button
            type="button"
            onClick={() => setStatus("paid")}
            disabled={busy === "paid"}
            data-testid="mark-paid-button"
            className="inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-4 py-2 text-[12px] font-semibold text-emerald-800 transition-colors duration-150 hover:bg-emerald-100"
          >
            <BadgeCheck className="h-3.5 w-3.5" strokeWidth={1.8} />
            Mark paid
          </button>
        )}
        {invoice.status === "draft" && (
          <button
            type="button"
            onClick={() => setStatus("sent")}
            disabled={busy === "sent"}
            data-testid="mark-sent-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
            Mark issued
          </button>
        )}
        {invoice.status === "paid" && (
          <button
            type="button"
            onClick={() => setStatus("sent")}
            data-testid="reopen-button"
            className="border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            Reopen as unpaid
          </button>
        )}
        {invoice.status !== "cancelled" && invoice.status !== "written_off" && (
          <Link
            to={`/invoices/${invoiceId}/edit`}
            data-testid="edit-invoice-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
            Edit
          </Link>
        )}
        {!["draft", "cancelled", "written_off"].includes(invoice.status) && (
          <button
            type="button"
            onClick={writeOff}
            disabled={busy === "write-off"}
            data-testid="write-off-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-60"
          >
            {busy === "write-off" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Ban className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
            Write off
          </button>
        )}
        {invoice.status === "written_off" && (
          <button
            type="button"
            onClick={restore}
            disabled={busy === "restore"}
            data-testid="restore-invoice-button"
            className="inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-4 py-2 text-[12px] font-semibold text-emerald-800 transition-colors duration-150 hover:bg-emerald-100 disabled:opacity-60"
          >
            {busy === "restore" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Undo2 className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
            Restore to the books
          </button>
        )}
        {invoice.is_adhoc && (
          <Link
            to={`/invoices/new?copy=${invoiceId}`}
            data-testid="duplicate-invoice-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
            Duplicate
          </Link>
        )}
        {isOwner && (
        <button
          type="button"
          onClick={remove}
          data-testid="delete-invoice-button"
          className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-600 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
          Delete
        </button>
        )}
      </div>

      {revisionPending && (
        <div
          className="no-print flex flex-col gap-3 border border-amber-300 bg-amber-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
          data-testid="revision-banner"
        >
          <p className="text-[13px] leading-relaxed text-amber-900">
            This fee note was edited after it went out. Send it again and the email will be marked{" "}
            <span className="font-semibold">Revised</span> for every contact person on the matter.
          </p>
          <button
            type="button"
            onClick={sendEmail}
            disabled={busy === "email"}
            data-testid="resend-revised-button"
            className="inline-flex shrink-0 items-center gap-2 bg-slate-900 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
          >
            {busy === "email" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
            Resend as revised
          </button>
        </div>
      )}

      <div className="grid min-w-0 gap-6 lg:grid-cols-3">
        <section className="surface min-w-0 p-5 sm:p-7 lg:col-span-2" data-testid="invoice-preview">
          <div className="border-b border-slate-900 pb-5">
            <h2 className="font-serif text-2xl text-slate-900">
              {settings.counsel_name || "Set your name in Chambers Settings"}
            </h2>
            <p className="mt-1 text-[12px] text-slate-500">
              {[settings.designation, settings.chambers_name].filter(Boolean).join(" · ")}
            </p>
            <p className="mt-2 whitespace-pre-line text-[11px] text-slate-500">{settings.address}</p>
            {settings.tax_id ? (
              <p className="num mt-2 text-[11px] text-slate-500">
                {settings.tax_label} No. {settings.tax_id}
              </p>
            ) : null}
          </div>

          <dl className="mt-6 grid gap-5 sm:grid-cols-2">
            <div className="min-w-0 space-y-4 break-words">
              <Row
                label="Billed to"
                value={
                  <>
                    <span className="font-semibold">
                      {invoice.billed_to?.name || contact.full_name || "—"}
                    </span>
                    {(invoice.billed_to?.designation || contact.designation) ? (
                      <span className="block text-slate-500">
                        {invoice.billed_to?.designation || contact.designation}
                      </span>
                    ) : null}
                    {(invoice.billed_to?.org || firm?.name) ? (
                      <span className="block text-slate-500">
                        {invoice.billed_to?.org || firm?.name}
                      </span>
                    ) : null}
                    {invoice.billed_to?.address ? (
                      <span className="block whitespace-pre-line text-slate-500">
                        {invoice.billed_to.address}
                      </span>
                    ) : null}
                    {(invoice.billed_to?.email || contact.email) ? (
                      <span className="block text-slate-500">
                        {invoice.billed_to?.email || contact.email}
                      </span>
                    ) : null}
                    {(invoice.billed_to?.phone || contact.whatsapp) ? (
                      <span className="num block text-slate-500">
                        {invoice.billed_to?.phone || contact.whatsapp}
                      </span>
                    ) : null}
                    {invoice.billed_to?.tax_id ? (
                      <span className="num block text-slate-500">
                        {settings.tax_label} No. {invoice.billed_to.tax_id}
                      </span>
                    ) : null}
                  </>
                }
              />
              {invoice.party_display !== "billing" && (client.name || matter.client_name) ? (
                <Row
                  label="Client"
                  value={
                    <span data-testid="detail-client-block">
                      <span className="font-semibold">{client.name || matter.client_name}</span>
                      {matter.party_role ? (
                        <span className="block text-slate-500">{matter.party_role}</span>
                      ) : null}
                      {client.address ? (
                        <span className="block whitespace-pre-line text-slate-500">
                          {client.address}
                        </span>
                      ) : null}
                      {client.city ? <span className="block text-slate-500">{client.city}</span> : null}
                      {client.email ? <span className="block text-slate-500">{client.email}</span> : null}
                      {client.phone ? (
                        <span className="num block text-slate-500">{client.phone}</span>
                      ) : null}
                      {client.tax_id ? (
                        <span className="num block text-slate-500">
                          {settings.tax_label} No. {client.tax_id}
                        </span>
                      ) : null}
                    </span>
                  }
                />
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Row label="Invoice date" value={formatDate(invoice.issue_date)} />
              <Row label="Due date" value={formatDate(invoice.due_date)} />
              <Row label="Matter ref" value={matter.matter_ref} />
              <Row
                label="Represented party"
                value={`${matter.represented_party || "—"}${matter.party_role ? ` (${matter.party_role})` : ""}`}
              />
            </div>
          </dl>

          {fees.length > 0 && <ItemTable title="Professional fees" items={fees} symbol={symbol} />}
          {disbursements.length > 0 && (
            <ItemTable title="Disbursements & out-of-pocket" items={disbursements} symbol={symbol} />
          )}

          <div className="mt-7 flex justify-end">
            <dl className="w-full max-w-xs space-y-2 text-[13px]">
              <div className="flex justify-between text-slate-600">
                <dt>Professional fees</dt>
                <dd className="num text-slate-900">{money(invoice.fees_total, symbol)}</dd>
              </div>
              {Number(invoice.disbursements_total) > 0 && (
                <div className="flex justify-between text-slate-600">
                  <dt>Disbursements</dt>
                  <dd className="num text-slate-900">
                    {money(invoice.disbursements_total, symbol)}
                  </dd>
                </div>
              )}
              {Number(invoice.discount) > 0 && (
                <div className="flex justify-between text-slate-600">
                  <dt>Discount</dt>
                  <dd className="num text-slate-900">- {money(invoice.discount, symbol)}</dd>
                </div>
              )}
              <div className="flex justify-between text-slate-600">
                <dt>
                  {settings.tax_label} @ {Number(invoice.tax_rate)}%
                </dt>
                <dd className="num text-slate-900">{money(invoice.tax_amount, symbol)}</dd>
              </div>
              <div className="flex items-baseline justify-between border-t border-slate-900 pt-3">
                <dt className="label-caps text-slate-900">Total payable</dt>
                <dd className="num text-lg text-slate-900" data-testid="detail-total">
                  {money(invoice.total, symbol)}
                </dd>
              </div>
              {invoice.display_status === "paid" ? (
                <div className="flex justify-between text-emerald-700">
                  <dt>Received {formatDate(invoice.paid_date)}</dt>
                  <dd className="num">{money(invoice.amount_paid, symbol)}</dd>
                </div>
              ) : Number(invoice.amount_paid) > 0 ? (
                <>
                  <div className="flex justify-between text-emerald-700">
                    <dt>Received</dt>
                    <dd className="num">{money(invoice.amount_paid, symbol)}</dd>
                  </div>
                  <div className="flex justify-between font-semibold text-slate-900">
                    <dt>Balance due</dt>
                    <dd className="num" data-testid="detail-balance">
                      {money(balance, symbol)}
                    </dd>
                  </div>
                </>
              ) : null}
            </dl>
          </div>

          {invoice.notes ? (
            <p className="mt-8 whitespace-pre-line border-t border-slate-100 pt-5 text-[12px] text-slate-600">
              {invoice.notes}
            </p>
          ) : null}
        </section>

        <aside className="min-w-0 space-y-4">
          <div className="surface p-6" data-testid="payments-panel">
            <div className="flex items-baseline justify-between">
              <h2 className="font-serif text-xl text-slate-900">Payments</h2>
              <span className="label-caps">{payments.length} received</span>
            </div>

            <dl className="mt-5 space-y-2 text-[13px]">
              <div className="flex justify-between text-slate-600">
                <dt>Invoice total</dt>
                <dd className="num text-slate-900">{money(invoice.total, symbol)}</dd>
              </div>
              <div className="flex justify-between text-slate-600">
                <dt>Received</dt>
                <dd className="num text-emerald-700" data-testid="payments-received">
                  {money(invoice.amount_paid, symbol)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between border-t border-slate-900 pt-2.5">
                <dt className="label-caps text-slate-900">Balance due</dt>
                <dd className="num text-base text-slate-900" data-testid="payments-balance">
                  {money(balance, symbol)}
                </dd>
              </div>
            </dl>

            <div className="mt-4 h-1.5 w-full bg-slate-100">
              <div
                className="h-full bg-emerald-600 transition-all duration-500"
                style={{
                  width: `${Math.min(100, (Number(invoice.amount_paid) / Math.max(Number(invoice.total), 1)) * 100)}%`,
                }}
              />
            </div>

            {payments.length > 0 && (
              <ul className="mt-5 divide-y divide-slate-100 border-t border-slate-100">
                {payments
                  .slice((payPage - 1) * PER_PAGE, payPage * PER_PAGE)
                  .map((p) => (
                  <li
                    key={p.payment_id}
                    className="py-3"
                    data-testid={`payment-row-${p.payment_id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="num text-[13px] text-slate-900">{money(p.amount, symbol)}</p>
                        <p className="text-[11px] text-slate-400">
                          {formatDate(p.paid_on)}
                          {p.receipt_no ? ` · ${p.receipt_no}` : ""}
                          {p.mode ? ` · ${(PAY_MODES.find((m) => m.value === p.mode) || {}).label || p.mode}` : ""}
                          {p.reference ? ` · ${p.reference}` : ""}
                        </p>
                        {p.has_tds ? (
                          <p
                            className="num text-[11px] text-amber-700"
                            data-testid={`payment-tds-note-${p.payment_id}`}
                          >
                            {money(p.inward_amount, symbol)} in bank + {money(p.tds_amount, symbol)} TDS
                          </p>
                        ) : null}
                      </div>
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => removePayment(p.payment_id)}
                          disabled={busy === `del-${p.payment_id}`}
                          data-testid={`delete-payment-${p.payment_id}`}
                          className="flex h-7 w-7 shrink-0 items-center justify-center border border-slate-200 text-slate-400 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                        >
                          <Trash2 className="h-3 w-3" strokeWidth={1.8} />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => openPdf(`/payments/${p.payment_id}/receipt.pdf`)}
                        data-testid={`receipt-pdf-${p.payment_id}`}
                        className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600 transition-colors duration-150 hover:border-slate-900 hover:text-slate-900"
                      >
                        <FileText className="h-3 w-3" strokeWidth={1.8} />
                        Receipt
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          act(`rcpt-${p.payment_id}`, async () => {
                            const { data: res } = await api.post(
                              `/payments/${p.payment_id}/send-email`,
                            );
                            toast.success(`Receipt emailed to ${res.recipient}`);
                          })
                        }
                        disabled={busy === `rcpt-${p.payment_id}`}
                        data-testid={`receipt-email-${p.payment_id}`}
                        className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600 transition-colors duration-150 hover:border-slate-900 hover:text-slate-900 disabled:opacity-60"
                      >
                        {busy === `rcpt-${p.payment_id}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Mail className="h-3 w-3" strokeWidth={1.8} />
                        )}
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          act(`wa-${p.payment_id}`, async () => {
                            const { data: res } = await api.post(
                              `/payments/${p.payment_id}/whatsapp`,
                            );
                            openWhatsApp(res);
                          })
                        }
                        disabled={busy === `wa-${p.payment_id}`}
                        data-testid={`receipt-whatsapp-${p.payment_id}`}
                        className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-emerald-700 transition-colors duration-150 hover:border-emerald-600 disabled:opacity-60"
                      >
                        {busy === `wa-${p.payment_id}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <MessageCircle className="h-3 w-3" strokeWidth={1.8} />
                        )}
                        WhatsApp
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {payments.length > PER_PAGE ? (
              <div className="mt-4">
                <Pager
                  page={payPage}
                  total={payments.length}
                  onPage={setPayPage}
                  testId="payments-pager"
                />
              </div>
            ) : null}

            {invoice.status === "cancelled" || invoice.status === "written_off" ? null : balance > 0 ? (
              <form className="mt-5 border-t border-slate-100 pt-5" onSubmit={addPayment} data-testid="payment-form">
                {payForm.has_tds ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="label-caps">Received in bank</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={payForm.inward_amount}
                        onChange={(e) => setPayForm((f) => ({ ...f, inward_amount: e.target.value }))}
                        className="num rounded-sm"
                        data-testid="payment-inward-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-caps">TDS deducted</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={payForm.tds_amount}
                        onChange={(e) => setPayForm((f) => ({ ...f, tds_amount: e.target.value }))}
                        className="num rounded-sm"
                        data-testid="payment-tds-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label className="label-caps">Amount received</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={payForm.amount}
                      onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
                      placeholder={String(balance)}
                      className="num rounded-sm"
                      data-testid="payment-amount-input"
                    />
                  </div>
                )}

                <div className="mt-3 space-y-1.5">
                  <Label className="label-caps">Received on</Label>
                  <Input
                    type="date"
                    value={payForm.paid_on}
                    onChange={(e) => setPayForm((f) => ({ ...f, paid_on: e.target.value }))}
                    className="num rounded-sm"
                    data-testid="payment-date-input"
                  />
                </div>

                <label className="mt-3 flex items-center gap-2.5 text-[11px] uppercase tracking-[0.12em] text-slate-600">
                  <Switch
                    checked={!!payForm.has_tds}
                    onCheckedChange={(v) =>
                      setPayForm((f) => ({
                        ...f,
                        has_tds: v,
                        inward_amount: v ? f.inward_amount || f.amount : f.inward_amount,
                      }))
                    }
                    data-testid="payment-tds-toggle"
                  />
                  TDS deducted
                </label>
                {payForm.has_tds ? (
                  <p className="mt-2 text-[11px] text-slate-600" data-testid="payment-tds-credit">
                    Credited to this fee note:{" "}
                    <span className="num font-semibold text-slate-900">
                      {money(Number(payForm.inward_amount || 0) + Number(payForm.tds_amount || 0), symbol)}
                    </span>{" "}
                    · the receipt will note that it is subject to deposit and credit of TDS
                  </p>
                ) : null}

                <button
                  type="button"
                  onClick={() => setRemitOpen((v) => !v)}
                  data-testid="toggle-remittance-fields"
                  className="mt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 underline decoration-slate-300 transition-colors duration-150 hover:text-slate-900"
                >
                  {remitOpen ? "Hide remittance details" : "Add remittance details"}
                </button>

                {remitOpen ? (
                  <div className="mt-3 space-y-3 border border-slate-200 p-3" data-testid="remittance-fields">
                    <div className="space-y-1.5">
                      <Label className="label-caps">Mode</Label>
                      <Select
                        value={payForm.mode || "none"}
                        onValueChange={(v) =>
                          setPayForm((f) => ({ ...f, mode: v === "none" ? "" : v }))
                        }
                      >
                        <SelectTrigger className="rounded-sm" data-testid="payment-mode-select">
                          <SelectValue placeholder="Not stated" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not stated</SelectItem>
                          {PAY_MODES.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-caps">Reference / UTR no.</Label>
                      <Input
                        value={payForm.reference}
                        onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))}
                        className="num rounded-sm"
                        data-testid="payment-reference-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-caps">Remitter / bank</Label>
                      <Input
                        value={payForm.remitter}
                        onChange={(e) => setPayForm((f) => ({ ...f, remitter: e.target.value }))}
                        className="rounded-sm"
                        data-testid="payment-remitter-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-caps">Instrument date</Label>
                      <Input
                        type="date"
                        value={payForm.instrument_date}
                        onChange={(e) =>
                          setPayForm((f) => ({ ...f, instrument_date: e.target.value }))
                        }
                        className="num rounded-sm"
                        data-testid="payment-instrument-date-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-caps">Remark</Label>
                      <Input
                        value={payForm.remark}
                        onChange={(e) => setPayForm((f) => ({ ...f, remark: e.target.value }))}
                        className="rounded-sm"
                        data-testid="payment-remark-input"
                      />
                    </div>
                    <label className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.12em] text-slate-600">
                      <Switch
                        checked={!!payForm.show_remittance_on_receipt}
                        onCheckedChange={(v) =>
                          setPayForm((f) => ({ ...f, show_remittance_on_receipt: v }))
                        }
                        data-testid="payment-print-remittance-toggle"
                      />
                      Print these on the receipt
                    </label>
                  </div>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={busy === "payment"}
                    data-testid="record-payment-button"
                    className="inline-flex items-center gap-2 bg-slate-900 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
                  >
                    {busy === "payment" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                    )}
                    Record payment
                  </button>
                  <button
                    type="button"
                    onClick={() => setPayForm((f) => ({ ...f, amount: String(balance) }))}
                    data-testid="fill-balance-button"
                    className="border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
                  >
                    Full balance
                  </button>
                </div>
              </form>
            ) : (
              <p className="mt-5 border-t border-slate-100 pt-4 text-[11px] text-emerald-700">
                Settled in full{invoice.paid_date ? ` on ${formatDate(invoice.paid_date)}` : ""}.
              </p>
            )}
          </div>

          <div className="surface p-6" data-testid="dispatch-panel">
            <h2 className="font-serif text-xl text-slate-900">Dispatch</h2>
            <dl className="mt-5 space-y-4 text-[13px]">
              <div>
                <dt className="label-caps">Email goes to</dt>
                <dd className="mt-1 space-y-2 text-[13px]" data-testid="dispatch-recipients">
                  {recipients.length === 0 ? (
                    <span className="text-slate-800">{contact.email || "—"}</span>
                  ) : (
                    recipients.map((r) => (
                      <span key={r.contact_id} className="block">
                        <span className="text-slate-800">{r.full_name}</span>
                        {r.is_billing ? (
                          <span className="text-slate-400"> · billing contact</span>
                        ) : null}
                        <span
                          className={`block text-[11px] ${r.email ? "text-slate-400" : "text-red-600"}`}
                        >
                          {r.email || "no email on record"}
                        </span>
                      </span>
                    ))
                  )}
                </dd>
              </div>
              {copies.length > 0 ? (
                <Row
                  label="Copied to"
                  value={
                    <span data-testid="dispatch-cc">
                      {copies.map((address) => (
                        <span key={address} className="block text-slate-600">
                          {address}
                        </span>
                      ))}
                    </span>
                  }
                />
              ) : null}
              <Row label="WhatsApp number" value={contact.whatsapp} />
              <Row
                label="Last channel used"
                value={invoice.last_channel ? invoice.last_channel : "Not yet dispatched"}
              />
              <Row
                label="Issued on"
                value={invoice.sent_at ? formatDate(invoice.sent_at.slice(0, 10)) : "—"}
              />
              <Row
                label="Reminders sent"
                value={
                  invoice.reminder_count
                    ? `${invoice.reminder_count} · last ${formatDate((invoice.last_reminder_at || "").slice(0, 10))}`
                    : "None"
                }
              />
            </dl>
            <p className="mt-5 border-t border-slate-100 pt-4 text-[11px] leading-relaxed text-slate-500">
              The email carries a secure link to this invoice PDF and goes to every contact person
              on the matter, the billing contact first
              {copies.length > 0 ? ", with your standing copies added" : ""}. WhatsApp opens a
              prefilled message to the billing contact with the same link.
            </p>
          </div>

          <div className="surface p-6">
            <h2 className="font-serif text-xl text-slate-900">Remittance</h2>
            <dl className="mt-5 space-y-3 text-[12px]">
              <Row label="Account name" value={settings.bank_account_name} />
              <Row label="Bank" value={settings.bank_name} />
              <Row label="Account number" value={settings.bank_account_number} />
              <Row label="IFSC / SWIFT" value={settings.bank_ifsc || settings.bank_swift} />
              {settings.upi_id ? <Row label="UPI" value={settings.upi_id} /> : null}
            </dl>
            <Link
              to="/settings"
              data-testid="edit-settings-link"
              className="mt-5 inline-block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
            >
              Edit letterhead &amp; bank details
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
